import { cn } from '@repo/tailwind-config/tailwind.functions'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { getJourneyPlaces } from '../../../api/journey-place/get-journey-places'
import { getOls } from '../../../api/ols/get-ols'
import { getOlJourneyPlaces } from '../../../api/ols/journey-places/get-ol-journey-places'
import { getUsers } from '../../../api/user/get-users'
import { AccomodationEnum } from '../../../lib/types/enums/accomodation-type/accomodation-type'
import {
  SchoolLevelEnum,
  schoolLevelEnumMapping
} from '../../../lib/types/enums/common/school-level.enum'
import { OLTypeEnum } from '../../../lib/types/enums/ol/ol-type.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { CenterInput } from '../../../lib/types/models/center/center-input'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import {
  getSelectedEnumOption,
  getSelectedOption
} from '../../../lib/utils/functions/misc.functions'
import { getOlName } from '../../../views/ols/center/utils/functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'

type Props = {
  mode?: 'global-view' | 'normal-view'
  restrictedAccomodation?: AccomodationEnum
}

const CENTER_OL_TYPES: OLTypeEnum[] = [OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE]

const CenterInformationForm = ({ mode = 'normal-view', restrictedAccomodation }: Props = {}) => {
  const form = useFormContext<CenterInput>()
  const olId = form.watch('olId')

  const { data: users } = useQuery({
    queryKey: ['users', 'admins'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } })
  })

  const { data: ols } = useQuery({
    queryKey: ['ols', { types: CENTER_OL_TYPES }],
    queryFn: () => getOls({ filter: { types: CENTER_OL_TYPES } })
  })

  const { data: journeyPlaces } = useQuery({
    queryKey: ['journey-places', `olId-${olId}`],
    queryFn: () => getJourneyPlaces({ filter: { olId } })
  })

  const { data: olJourneyPlaces } = useQuery({
    queryKey: ['ol-journey-places', `olId-${olId}`],
    queryFn: () => {
      if (!olId) return
      return getOlJourneyPlaces({ olId })
    },
    enabled: !!olId
  })

  return (
    <div className="flex flex-col gap-4">
      <div
        className={cn('flex gap-3', {
          'flex-col': mode === 'global-view'
        })}
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput required {...field} type="text" label="NOM du centre" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lecResponsableId"
          render={({ field }) => {
            return (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    options={users?.data.map((user) => ({
                      label: toFullName(user.firstName, user.lastName),
                      value: user.id
                    }))}
                    searchable
                    label={'Responsable secteur'}
                    selectedOption={getSelectedOption(
                      field.value,
                      toFullName(
                        users?.data.find((user) => user.id === field.value)?.firstName,
                        users?.data.find((user) => user.id === field.value)?.lastName
                      )
                    )}
                  />
                </FormControl>
              </FormItem>
            )
          }}
        />
      </div>
      <div
        className={cn('flex gap-3', {
          'flex-col': mode === 'global-view'
        })}
      >
        <FormField
          control={form.control}
          name="olId"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  options={ols?.data.map((ol) => ({
                    label: getOlName(ol),
                    value: ol.id
                  }))}
                  optional
                  searchable
                  label={'Prestataire étranger'}
                  selectedOption={getSelectedOption(
                    field.value,
                    getOlName(ols?.data.find((ol) => ol.id === field.value))
                  )}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="journeyPlaceId"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  searchable
                  disabled={!!restrictedAccomodation}
                  options={journeyPlaces?.data.map((journeyPlace) => ({
                    label: journeyPlace.name,
                    value: journeyPlace.id
                  }))}
                  optional
                  label={'Lieu de séjour'}
                  selectedOption={getSelectedOption(
                    field.value,
                    journeyPlaces?.data.find((tp) => tp.id === field.value)?.name
                  )}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="olJourneyPlaceId"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  options={olJourneyPlaces?.data.map((olJourneyPlace) => ({
                    label:
                      CenterProperties.getCenterTeachingCompleteAddress(olJourneyPlace)?.name ??
                      '',
                    value: olJourneyPlace.id
                  }))}
                  optional
                  label={'Adresse du centre'}
                  selectedOption={getSelectedOption(
                    field.value,
                    CenterProperties.getCenterTeachingCompleteAddress(
                      olJourneyPlaces?.data.find((tp) => tp.id === field.value)
                    )?.name
                  )}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div
        className={cn('grid grid-cols-4 gap-3', {
          'grid-cols-2': mode === 'global-view'
        })}
      >
        <FormField
          control={form.control}
          name="minAge"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput {...field} type="number" label="Age minimum" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="maxAge"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput {...field} type="number" label="Age maximum" />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="minSchoolLevel"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  dropDownPosition={mode === 'global-view' ? 'above' : 'under'}
                  options={Object.values(SchoolLevelEnum).map((schoolLevel) => ({
                    label: schoolLevelEnumMapping[schoolLevel],
                    value: schoolLevel
                  }))}
                  label={'Classe min'}
                  selectedOption={getSelectedEnumOption(field.value, schoolLevelEnumMapping)}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="maxSchoolLevel"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  label={'Classe max'}
                  dropDownPosition={mode === 'global-view' ? 'above' : 'under'}
                  options={Object.values(SchoolLevelEnum).map((schoolLevel) => ({
                    label: schoolLevelEnumMapping[schoolLevel],
                    value: schoolLevel
                  }))}
                  selectedOption={getSelectedEnumOption(field.value, schoolLevelEnumMapping)}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}

export default CenterInformationForm
